import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';

import BackButton from '../../../../components/shared/BackButton/BackButton';
import Button from '../../../../components/shared/Button/Button';
import { BOON_VALUES_OPTIONS, TEMPLATE_URL } from '../../../../constants/contactsConstants';
import { importContacts, uploadCSV, uploadMapping, uploadVCF } from '../../../../redux/actions/networkActions';
import urls from '../../../../routing/urls';

import CancelModal from './CancelModal/CancelModal';
import HeadersModal from './HeadersModal/HeadersModal';
import ImportResultModal from './ImportResultModal/ImportResultModal';
import ImportSuccess from './ImportSuccess/ImportSuccess';
import Mapping from './Mapping/Mapping';
import MappingSummary from './MappingSummary/MappingSummary';
import SelectFile from './SelectFile/SelectFile';

import styles from './CSVImport.module.scss';

const TABS = {
  selectFile: 'select-file',
  mapping: 'mapping',
  mappingSummary: 'mapping-summary',
  success: 'success',
};

const CSVImport = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.selectFile);
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [cancelModal, setCancelModal] = useState(false);
  const [importResultModal, setImportResultModal] = useState({ modal: false, invalid: 0, valid: 0, total: 0 });
  const [headersModal, setHeadersModal] = useState({ show: false, data: [] });
  const [importedHeaders, setImportedHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [sample, setSample] = useState([]);

  useEffect(() => {
    const fieldMappingArray = [];
    const customFieldsArray = [];
    importedHeaders.forEach((header, index) => {
      fieldMappingArray.push({
        label: BOON_VALUES_OPTIONS.find((i) => i.label === header.trim().replace('*', ''))?.label,
        value: BOON_VALUES_OPTIONS.find((i) => i.label === header.trim().replace('*', ''))?.value,
        importValue: header,
        customValue: '',
        id: index,
      });
      customFieldsArray.push(false);
    });
    setFieldMapping(fieldMappingArray);
    setCustomFields(customFieldsArray);
  }, [importedHeaders]);

  const handleFileDelete = useCallback((file) => {
    setAcceptedFiles((prevState) => {
      const files = [...prevState];
      files.splice(prevState.indexOf(file), 1);
      return files;
    });
  }, []);

  const handleOnDrop = useCallback((files) => {
    setAcceptedFiles([...files]);
  }, []);

  const handleBackBtn = useCallback(() => {
    if (activeTab === TABS.selectFile) {
      navigate(urls.syncSources);
    } else if (activeTab === TABS.mapping) {
      setActiveTab(TABS.selectFile);
    } else if (activeTab === TABS.mappingSummary) {
      setActiveTab(TABS.mapping);
    } else if (activeTab === TABS.success) {
      navigate(urls.network);
    }
  }, [activeTab]);

  const uploadSuccessCallback = useCallback((data) => {
    setImportedHeaders(data.imported_headers);
    if (data.header_not_exist.length) {
      setHeadersModal({ show: true, data: data.header_not_exist });
    } else {
      setActiveTab(TABS.mapping);
    }
  }, []);

  const uploadVCFSuccessCallback = useCallback(() => {
    setActiveTab(TABS.success);
  }, []);

  const handleFileUpload = useCallback(() => {
    const file = acceptedFiles[0];
    const { path } = file;
    const splittedPath = path.split('.');
    const extension = splittedPath[splittedPath.length - 1];
    if (extension === 'vcf') {
      dispatch(uploadVCF(file, uploadVCFSuccessCallback));
    } else dispatch(uploadCSV(file, uploadSuccessCallback));
  }, [acceptedFiles]);

  const toggleCancelModal = useCallback(() => {
    setCancelModal((prevState) => !prevState);
  }, []);

  const toggleHeadersModal = useCallback((data) => {
    setHeadersModal((prevState) => (prevState.show ? { show: false, data: [] } : { show: true, data }));
  }, []);

  const navigateToMapping = useCallback(() => {
    setActiveTab(TABS.mapping);
    setHeadersModal({ show: false, data: [] });
  }, []);

  const mappingSuccessCallback = useCallback((response) => {
    setActiveTab(TABS.mappingSummary);
    setSample(response.sample_values);
  }, []);

  const importSuccessCallback = useCallback((response) => {
    const { invalid, valid, total_count } = response;
    if (invalid) {
      setImportResultModal({ modal: true, valid, invalid, total: total_count });
    }
    setActiveTab(TABS.success);
  }, []);

  const handleUploadMapping = useCallback(() => {
    const formattedFieldMapping = fieldMapping.map((field) => ({
      importValue: field.importValue,
      boonValue: customFields[field.id] ? field.customValue : field.value,
    }));
    dispatch(uploadMapping(acceptedFiles[0], formattedFieldMapping, mappingSuccessCallback));
  }, [fieldMapping, acceptedFiles]);

  const handleImport = useCallback(() => {
    const formattedFieldMapping = fieldMapping.map((field) => ({
      importValue: field.importValue,
      boonValue: customFields[field.id] ? field.customValue : field.value,
    }));
    const now = moment().format('MMM Do YYYY h:mmA');
    dispatch(importContacts(acceptedFiles[0], formattedFieldMapping, now, importSuccessCallback));
  }, [acceptedFiles, fieldMapping]);

  const handleCloseImportResultModal = useCallback(() => {
    const { valid } = importResultModal;
    if (valid === 0) {
      return navigate(urls.network);
    }
    setImportResultModal({ modal: false, invalid: 0, valid: 0, total: 0 });
  }, [importResultModal]);

  const RenderActions = useMemo(() => {
    let buttons = [];
    if (activeTab === TABS.selectFile) {
      buttons = [
        {
          title: t('cancel'),
          action: toggleCancelModal,
          className: styles.cancelBtn,
        },
        {
          title: t('next'),
          action: handleFileUpload,
          disabled: acceptedFiles.length === 0,
        },
      ];
    }
    if (activeTab === TABS.mapping) {
      buttons = [
        {
          title: t('cancel'),
          action: toggleCancelModal,
          className: styles.cancelBtn,
        },
        {
          title: t('next'),
          action: handleUploadMapping,
        },
      ];
    }
    if (activeTab === TABS.mappingSummary) {
      buttons = [
        {
          title: t('cancel'),
          action: toggleCancelModal,
          className: styles.cancelBtn,
        },
        {
          title: t('import'),
          action: handleImport,
        },
      ];
    }
    return (
      <div className={styles.actionsCont}>
        {buttons.map(({ title, action, className, disabled }) => (
          <Button className={classNames(styles.btn, className)} onClick={action} disabled={disabled} key={title}>
            {title}
          </Button>
        ))}
      </div>
    );
  }, [activeTab, acceptedFiles, fieldMapping]);

  return (
    <div className={styles.rootCont}>
      <CancelModal
        showModal={cancelModal}
        toggleModal={toggleCancelModal}
        handleContinue={() => navigate(urls.syncSources)}
      />
      <HeadersModal
        showModal={headersModal.show}
        toggleModal={toggleHeadersModal}
        handleContinue={navigateToMapping}
        data={headersModal.data}
        TEMPLATE_URL={TEMPLATE_URL}
      />
      <ImportResultModal
        showModal={importResultModal.modal}
        valid={importResultModal.valid}
        invalid={importResultModal.invalid}
        total={importResultModal.total}
        toggleModal={handleCloseImportResultModal}
      />
      <div className={styles.backCont}>
        <BackButton onBack={handleBackBtn} />
      </div>
      <div className={styles.contentCont}>
        {activeTab === TABS.selectFile && (
          <SelectFile
            acceptedFiles={acceptedFiles}
            handleFileDelete={handleFileDelete}
            handleOnDrop={handleOnDrop}
            TEMPLATE_URL={TEMPLATE_URL}
          />
        )}
        {activeTab === TABS.mapping && (
          <Mapping
            file={acceptedFiles[0]}
            importedHeaders={importedHeaders}
            fieldMapping={fieldMapping}
            setFieldMapping={setFieldMapping}
            customFields={customFields}
            setCustomFields={setCustomFields}
          />
        )}
        {activeTab === TABS.mappingSummary && <MappingSummary file={acceptedFiles[0]} sample={sample} />}
        {activeTab === TABS.success && <ImportSuccess />}
      </div>
      {RenderActions}
    </div>
  );
};

export default CSVImport;
